import { Injectable, TemplateRef } from '@angular/core';
import { NgbToastConfig } from '@ng-bootstrap/ng-bootstrap';

export interface Toast {
  classname?: string;
  delay: number;
  message?: string;
  title?: string;
  icon?: string;
}

@Injectable({
  providedIn: 'root'
})
export class ToastService {

  toasts: Toast[] = [];
  delay = 4000;

  private show(toast: Toast) {
    this.toasts.push(toast);
  }

  remove(toast: Toast) {
    this.toasts = this.toasts.filter((t) => t !== toast);
  }

  clear() {
    this.toasts.splice(0, this.toasts.length);
  }

  /**
   * Muestra un mensaje de éxito en un toast.
   * 
   * @param message Mensaje a mostrar en el toast.
   * @param title (Opcional) Título del toast.
   * @param delay (Opcional) Tiempo en milisegundos antes de que el toast desaparezca. 
   *        Por defecto, usa el valor definido en `this.delay`.
   */
  success(message: string, title?: string, delay: number = this.delay) {
    this.show({ message, title, classname: 'toast-success', icon: 'bi-check-circle-fill', delay });
  }

  /**
   * Muestra un mensaje de error en un toast.
   * 
   * @param message Mensaje a mostrar en el toast.
   * @param title (Opcional) Título del toast.
   * @param delay (Opcional) Tiempo en milisegundos antes de que el toast desaparezca. 
   *        Por defecto, usa el valor definido en `this.delay`.
   */
  danger(message: string, title?: string, delay: number = this.delay) {
    this.show({ message, title, classname: 'toast-danger', icon: 'bi-x-circle-fill', delay });
  }

  /**
   * Muestra un mensaje informativo en un toast.
   * 
   * @param message Mensaje a mostrar en el toast.
   * @param title (Opcional) Título del toast.
   * @param delay (Opcional) Tiempo en milisegundos antes de que el toast desaparezca. 
   *        Por defecto, usa el valor definido en `this.delay`.
   */
  info(message: string, title?: string, delay: number = this.delay) {
    this.show({ message, title, classname: 'toast-info', icon: 'bi-info-circle-fill', delay });
  }

  /**
   * Muestra un toast con opciones personalizadas.
   * 
   * @param message Mensaje a mostrar en el toast.
   * @param title (Opcional) Título del toast.
   * @param classname (Opcional) Clase CSS para personalizar el estilo del toast. Debe contener
   *   - `color`.
   *   - `background-color`.
   *   - `border-color`.
   * @param icon (Opcional) Ícono de Bootstrap a mostrar en el toast.
   * @param delay (Opcional) Tiempo en milisegundos antes de que el toast desaparezca. 
   *        Por defecto, usa el valor definido en `this.delay`.
   */
  toast(message: string, title?: string, classname?: string, icon?: string, delay: number = this.delay) {
    this.show({ message, title, classname, icon, delay });
  }

  /**
   * Muestra un mensaje de advertencia en un toast.
   * 
   * @param message Mensaje a mostrar en el toast.
   * @param title (Opcional) Título del toast.
   * @param delay (Opcional) Tiempo en milisegundos antes de que el toast desaparezca. 
   */
  warning(message: string, title?: string, delay: number = this.delay) {
    this.show({ message, title, classname: 'toast-warning', icon: 'bi-exclamation-triangle-fill', delay });
  }

}
